import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { throwFromResponse } from '../lib/apiErrorMessage';
import type {
  MileageEarnPolicy,
  MileageEarnPolicyCreatePayload,
  MileageEarnPolicyUpdatePayload,
  MileagePolicyScope,
} from '../types';

const BASE = '/api/admin/v1/mileage-earn-policies';

interface ListEnvelope {
  success: boolean;
  data?: MileageEarnPolicy[] | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: MileageEarnPolicy | null;
}

export const earnPoliciesQueryKey = (scope: MileagePolicyScope) =>
  ['admin', 'mileage-earn-policies', scope] as const;

export const earnPolicyQueryKey = (policyId: string) =>
  ['admin', 'mileage-earn-policy', policyId] as const;

export const earnPolicyByProductQueryKey = (productId: string) =>
  ['admin', 'mileage-earn-policy', 'by-product', productId] as const;

const fetchList = async (scope: MileagePolicyScope): Promise<MileageEarnPolicy[]> => {
  const res = await adminFetch(`${BASE}?scope=${scope}`);
  if (!res.ok) await throwFromResponse(res, '적립 정책 목록을 불러오지 못했습니다.');
  const env = (await res.json()) as ListEnvelope;
  return env.data ?? [];
};

const fetchOne = async (policyId: string): Promise<MileageEarnPolicy> => {
  const res = await adminFetch(`${BASE}/${policyId}`);
  if (!res.ok) await throwFromResponse(res, '적립 정책을 불러오지 못했습니다.');
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('적립 정책을 찾을 수 없습니다.');
  return env.data;
};

const fetchByProduct = async (productId: string): Promise<MileageEarnPolicy[]> => {
  const res = await adminFetch(`${BASE}/by-product/${productId}`);
  if (!res.ok) await throwFromResponse(res, '상품 적립 정책을 불러오지 못했습니다.');
  const env = (await res.json()) as ListEnvelope;
  return env.data ?? [];
};

export const useMileageEarnPoliciesQuery = (scope: MileagePolicyScope) =>
  useQuery<MileageEarnPolicy[]>({
    queryKey: earnPoliciesQueryKey(scope),
    queryFn: () => fetchList(scope),
  });

export const useMileageEarnPolicyQuery = (policyId: string | null) =>
  useQuery<MileageEarnPolicy>({
    queryKey: earnPolicyQueryKey(policyId ?? ''),
    queryFn: () => fetchOne(policyId as string),
    enabled: Boolean(policyId),
  });

export const useMileageEarnPolicyByProductQuery = (productId: string | undefined) =>
  useQuery<MileageEarnPolicy[]>({
    queryKey: earnPolicyByProductQueryKey(productId ?? ''),
    queryFn: () => fetchByProduct(productId as string),
    enabled: Boolean(productId),
  });

export const useMileageEarnPolicyMutation = () => {
  const qc = useQueryClient();
  const invalidateLists = () =>
    qc.invalidateQueries({ queryKey: ['admin', 'mileage-earn-policies'] });

  const create = useMutation({
    mutationFn: async (payload: MileageEarnPolicyCreatePayload): Promise<MileageEarnPolicy> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwFromResponse(res, '적립 정책 등록에 실패했습니다.');
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('적립 정책 등록 응답이 비어 있습니다.');
      return env.data;
    },
    onSuccess: invalidateLists,
  });

  const update = useMutation({
    mutationFn: async ({
      policyId,
      payload,
    }: {
      policyId: string;
      payload: MileageEarnPolicyUpdatePayload;
    }): Promise<MileageEarnPolicy> => {
      const res = await adminFetch(`${BASE}/${policyId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwFromResponse(res, '적립 정책 수정에 실패했습니다.');
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('적립 정책 수정 응답이 비어 있습니다.');
      return env.data;
    },
    onSuccess: (_data, { policyId }) => {
      void qc.invalidateQueries({ queryKey: earnPolicyQueryKey(policyId) });
      invalidateLists();
    },
  });

  const softDelete = useMutation({
    mutationFn: async (policyId: string): Promise<void> => {
      const res = await adminFetch(`${BASE}/${policyId}`, {
        method: 'DELETE',
      });
      if (!res.ok) await throwFromResponse(res, '적립 정책 삭제에 실패했습니다.');
    },
    onSuccess: invalidateLists,
  });

  const markDefault = useMutation({
    mutationFn: async (policyId: string): Promise<MileageEarnPolicy> => {
      const res = await adminFetch(`${BASE}/${policyId}/default`, {
        method: 'POST',
      });
      if (!res.ok) await throwFromResponse(res, '기본 정책 설정에 실패했습니다.');
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('기본 정책 설정 응답이 비어 있습니다.');
      return env.data;
    },
    onSuccess: invalidateLists,
  });

  return { create, update, softDelete, markDefault };
};

/**
 * Replace the product's PRODUCT-scope earn policy
 * (PUT /api/admin/v1/mileage-earn-policies/by-product/{productId}).
 * policyId null = 매핑 해제. 쿠폰 by-product sync 와 동일하게 상품 저장 onSuccess 이후 호출.
 */
export const useSyncProductEarnPolicyMutation = () => {
  const qc = useQueryClient();
  return useMutation<MileageEarnPolicy[], Error, { productId: string; policyId: string | null }>({
    mutationFn: async ({ productId, policyId }) => {
      const path = `${BASE}/by-product/${productId}`;
      const body = { policyId };
      const headers = { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) };
      const res = await adminFetch(path, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) await throwFromResponse(res, '상품 적립 정책 동기화에 실패했습니다.');
      const env = (await res.json()) as ListEnvelope;
      return env.data ?? [];
    },
    onSuccess: (_data, { productId }) => {
      void qc.invalidateQueries({ queryKey: earnPolicyByProductQueryKey(productId) });
    },
  });
};
